/**
 * Hệ thống (System) route family — Plans 7.2 decomposition.
 * All routes are direct children of _authenticated (authenticatedRoute).
 */
import { lazy } from "react";
import { createRoute } from "@tanstack/react-router";

import { authenticatedRoute } from "../__root.js";
import { withSuspense } from "../route-helpers.js";

// ── Lazy page imports ────────────────────────────────────────────────

const FeatureFlagsPage = lazy(() =>
  import("@/features/system/feature-flags-page").then((mod) => ({ default: mod.FeatureFlagsPage })),
);
const AuditLogsPage = lazy(() =>
  import("@/features/system/audit-logs-page").then((mod) => ({ default: mod.AuditLogsPage })),
);
const HealthPage = lazy(() =>
  import("@/features/system/health-page").then((mod) => ({ default: mod.HealthPage })),
);
const SettingsPage = lazy(() =>
  import("@/features/settings").then((mod) => ({ default: mod.SettingsPage })),
);
const CalendarEventsPage = lazy(() =>
  import("@/features/calendar").then((mod) => ({ default: mod.CalendarEventsPage })),
);
const SearchOpsPage = lazy(() =>
  import("@/features/workspaces/module-pages").then((mod) => ({ default: mod.SearchOpsPage })),
);
const NotificationsPage = lazy(() =>
  import("@/features/notifications").then((mod) => ({ default: mod.NotificationsPage })),
);
const VolunteersPage = lazy(() =>
  import("@/features/volunteers").then((mod) => ({ default: mod.VolunteersPage })),
);
const ContactInfoPage = lazy(() =>
  import("@/features/contact-info").then((mod) => ({ default: mod.ContactInfoPage })),
);
const DesignSystemPage = lazy(() =>
  import("@/features/design-system").then((mod) => ({ default: mod.DesignSystemPage })),
);

// ── Detail / Create page imports ─────────────────────────────────────

const VolunteerCreatePage = lazy(() =>
  import("@/features/volunteers/volunteer-create-page").then((mod) => ({ default: mod.VolunteerCreatePage })),
);
const VolunteerDetailPage = lazy(() =>
  import("@/features/volunteers/volunteer-detail-page").then((mod) => ({ default: mod.VolunteerDetailPage })),
);

// ── Routes ───────────────────────────────────────────────────────────

const featureFlagsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/he-thong/feature-flags",
  component: withSuspense(FeatureFlagsPage),
});

const auditLogsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/he-thong/audit-logs",
  component: withSuspense(AuditLogsPage),
});

const settingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/he-thong/cai-dat",
  component: withSuspense(SettingsPage),
});

const calendarRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/he-thong/lich",
  component: withSuspense(CalendarEventsPage),
});

const searchRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/he-thong/tim-kiem",
  component: withSuspense(SearchOpsPage),
});

const notificationsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/he-thong/thong-bao",
  component: withSuspense(NotificationsPage),
});

const volunteersRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/he-thong/phung-su-vien",
  component: withSuspense(VolunteersPage),
});
const volunteersCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/he-thong/phung-su-vien/tao-moi",
  component: withSuspense(VolunteerCreatePage),
});
const volunteersDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/he-thong/phung-su-vien/$publicId",
  component: withSuspense(VolunteerDetailPage),
});

const contactInfoRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/he-thong/thong-tin-lien-he",
  component: withSuspense(ContactInfoPage),
});

const healthRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/he-thong/health",
  component: withSuspense(HealthPage),
});

const designSystemRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/he-thong/design-system",
  component: withSuspense(DesignSystemPage),
});

// System landing (canon)
const systemRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/he-thong",
  component: withSuspense(SettingsPage),
});

// Calendar detail workspace alias (canon compatibility)
const calendarDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/he-thong/lich/$eventId",
  component: withSuspense(CalendarEventsPage),
});

export const heThongRoutes = [
  featureFlagsRoute,
  auditLogsRoute,
  settingsRoute,
  systemRoute,
  calendarRoute,
  calendarDetailRoute,
  searchRoute,
  notificationsRoute,
  volunteersRoute,
  volunteersCreateRoute,
  volunteersDetailRoute,
  contactInfoRoute,
  healthRoute,
  designSystemRoute,
];
